import { AlertTriangle, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import { DateInput } from '@/components/ui/date-input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FormField } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Stepper } from '@/components/ui/stepper';
import { useAdminQuestionnaireTemplates } from '@/features/admin-questionnaire-templates/api';

import { useBulkSendLaunch, useBulkSendPreview, type BulkSendComposeInput } from '../api';
import {
  EMPTY_FILTER,
  isMatchAll,
  LIFE_STATUS_VALUES,
  SEX_VALUES,
  type BulkSendPatientFilter,
} from '../filterRequest';

const STEP_COMPOSE = 0;
const STEP_REVIEW = 1;

/**
 * Two-step create dialog for a bulk send (PTV2-1162): Compose (template + expiry + patient filter) →
 * Review (sendable vs excluded from the preview). On launch the job starts and the dialog closes; the
 * result is reviewed in the Bulk Send tab's job tables. Stepper anatomy mirrors the new-patient /
 * new-family wizards, wrapped in a Dialog.
 */
export function BulkSendCreateDialog({
  open,
  onOpenChange,
  onLaunched,
}: {
  readonly open: boolean;
  readonly onOpenChange: (next: boolean) => void;
  /** Called after a successful launch so the parent can focus/refresh the active-jobs table. */
  readonly onLaunched: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const [step, setStep] = useState(STEP_COMPOSE);
  const [templateId, setTemplateId] = useState('');
  const [filter, setFilter] = useState<BulkSendPatientFilter>(EMPTY_FILTER);
  const [templateError, setTemplateError] = useState<string | undefined>(undefined);

  const templatesQuery = useAdminQuestionnaireTemplates();
  const templates = useMemo(
    () => (templatesQuery.data ?? []).filter((tmpl) => tmpl.active),
    [templatesQuery.data],
  );

  const preview = useBulkSendPreview();
  const launch = useBulkSendLaunch();

  useEffect(() => {
    if (open) {
      setStep(STEP_COMPOSE);
      setTemplateId('');
      setFilter(EMPTY_FILTER);
      setTemplateError(undefined);
      preview.reset();
      launch.reset();
    }
    // Only re-run on open toggle; the mutations are stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function setField<K extends keyof BulkSendPatientFilter>(key: K, value: BulkSendPatientFilter[K]): void {
    setFilter((prev) => ({ ...prev, [key]: value }));
  }

  // Expiry is hidden until the backend wiring lands; send no expiry for now.
  const input: BulkSendComposeInput = { filter, templateId, expiresAt: '' };

  function handleNext(): void {
    if (templateId.length === 0) {
      setTemplateError(t('routes.questionnaires.bulkSend.create.errors.templateRequired'));
      return;
    }
    setTemplateError(undefined);
    preview.mutate(input);
    setStep(STEP_REVIEW);
  }

  function handleLaunch(): void {
    launch.mutate(input, {
      onSuccess: () => {
        onLaunched();
        onOpenChange(false);
      },
    });
  }

  const fieldPrefix = 'routes.questionnaires.bulkSend.create.fields';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl" disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('routes.questionnaires.bulkSend.create.title')}</DialogTitle>
        </DialogHeader>

        <div className="mx-auto w-full max-w-md py-2">
          <Stepper
            steps={[
              t('routes.questionnaires.bulkSend.create.steps.compose'),
              t('routes.questionnaires.bulkSend.create.steps.review'),
            ]}
            currentStep={step}
            aria-label={t('routes.questionnaires.bulkSend.create.title')}
          />
        </div>

        <div className="max-h-[60vh] space-y-4 overflow-y-auto px-1 py-2">
          {step === STEP_COMPOSE && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <FormField
                id="bulk-send-template"
                label={t(`${fieldPrefix}.template`)}
                required
                error={templateError}
              >
                {(fp) => (
                  <Select
                    {...fp}
                    value={templateId}
                    onChange={(e) => {
                      setTemplateId(e.target.value);
                    }}
                  >
                    <option value="">{t('routes.questionnaires.bulkSend.create.fields.templatePlaceholder')}</option>
                    {templates.map((tmpl) => (
                      <option key={tmpl.id} value={tmpl.id}>
                        {tmpl.label}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>

              <div className="sm:col-span-2 border-t border-border pt-3">
                <p className="mb-2 text-sm font-medium text-foreground">
                  {t('routes.questionnaires.bulkSend.create.recipientsHeading')}
                </p>
                <p className="mb-3 text-xs text-muted-foreground">
                  {t('routes.questionnaires.bulkSend.create.recipientsHint')}
                </p>
              </div>

              <FormField id="bulk-send-last-name" label={t(`${fieldPrefix}.lastName`)}>
                {(fp) => (
                  <Input
                    {...fp}
                    value={filter.lastName}
                    onChange={(e) => {
                      setField('lastName', e.target.value);
                    }}
                    autoComplete="off"
                  />
                )}
              </FormField>
              <FormField id="bulk-send-first-name" label={t(`${fieldPrefix}.firstName`)}>
                {(fp) => (
                  <Input
                    {...fp}
                    value={filter.firstName}
                    onChange={(e) => {
                      setField('firstName', e.target.value);
                    }}
                    autoComplete="off"
                  />
                )}
              </FormField>

              <FormField id="bulk-send-dob-from" label={t(`${fieldPrefix}.dobFrom`)}>
                {() => (
                  <DateInput
                    value={filter.dobFrom}
                    onChange={(v) => {
                      setField('dobFrom', v);
                    }}
                  />
                )}
              </FormField>
              <FormField id="bulk-send-dob-to" label={t(`${fieldPrefix}.dobTo`)}>
                {() => (
                  <DateInput
                    value={filter.dobTo}
                    onChange={(v) => {
                      setField('dobTo', v);
                    }}
                  />
                )}
              </FormField>

              <FormField id="bulk-send-sex" label={t(`${fieldPrefix}.sex`)}>
                {(fp) => (
                  <Select
                    {...fp}
                    value={filter.sex}
                    onChange={(e) => {
                      setField('sex', e.target.value as BulkSendPatientFilter['sex']);
                    }}
                  >
                    <option value="">{t('routes.questionnaires.bulkSend.create.anyOption')}</option>
                    {SEX_VALUES.map((s) => (
                      <option key={s} value={s}>
                        {t(`routes.questionnaires.bulkSend.create.sexOptions.${s}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
              <FormField id="bulk-send-life-status" label={t(`${fieldPrefix}.lifeStatus`)}>
                {(fp) => (
                  <Select
                    {...fp}
                    value={filter.lifeStatus}
                    onChange={(e) => {
                      setField('lifeStatus', e.target.value as BulkSendPatientFilter['lifeStatus']);
                    }}
                  >
                    <option value="">{t('routes.questionnaires.bulkSend.create.anyOption')}</option>
                    {LIFE_STATUS_VALUES.map((s) => (
                      <option key={s} value={s}>
                        {t(`routes.questionnaires.bulkSend.create.lifeStatusOptions.${s}`)}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
            </div>
          )}

          {step === STEP_REVIEW && (
            <div className="space-y-4">
              {isMatchAll(filter) && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>{t('routes.questionnaires.bulkSend.create.review.matchAllWarning')}</span>
                </div>
              )}

              {preview.isPending && (
                <p className="text-sm text-muted-foreground">
                  {t('routes.questionnaires.bulkSend.create.review.loading')}
                </p>
              )}

              {preview.isError && (
                <p role="alert" className="text-sm text-red-600">
                  {t('routes.questionnaires.bulkSend.create.review.error', {
                    code: getErrorCodeForUi(preview.error),
                  })}
                </p>
              )}

              {preview.isSuccess && (
                <div className="space-y-3">
                  <p className="text-sm text-foreground">
                    {t('routes.questionnaires.bulkSend.create.review.sendable', {
                      count: preview.data.sendable,
                    })}
                  </p>
                  {preview.data.excluded > 0 && (
                    <div className="rounded-md border border-border bg-muted/40 p-3">
                      <p className="text-sm font-medium text-foreground">
                        {t('routes.questionnaires.bulkSend.create.review.excluded', {
                          count: preview.data.excluded,
                        })}
                      </p>
                      <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                        {preview.data.byReason.map((r) => (
                          <li key={r.reason}>
                            {t(`routes.questionnaires.bulkSend.create.review.reason.${r.reason}`, {
                              count: r.count,
                              defaultValue: `${String(r.count)} — ${r.reason}`,
                            })}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {launch.isError && (
                <p role="alert" className="text-sm text-red-600">
                  {t('routes.questionnaires.bulkSend.create.review.launchError', {
                    code: getErrorCodeForUi(launch.error),
                  })}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-border pt-3">
          {step === STEP_REVIEW ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setStep(STEP_COMPOSE);
              }}
              disabled={launch.isPending}
            >
              <ArrowLeft className="me-2 h-4 w-4" aria-hidden="true" />
              {t('routes.questionnaires.bulkSend.create.nav.back')}
            </Button>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
              }}
            >
              {t('routes.questionnaires.bulkSend.create.nav.cancel')}
            </Button>
          )}

          {step === STEP_COMPOSE ? (
            <Button type="button" onClick={handleNext}>
              {t('routes.questionnaires.bulkSend.create.nav.next')}
              <ArrowRight className="ms-2 h-4 w-4" aria-hidden="true" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleLaunch}
              loading={launch.isPending}
              disabled={!preview.isSuccess || preview.data.sendable === 0}
            >
              {t('routes.questionnaires.bulkSend.create.nav.send', {
                count: preview.isSuccess ? preview.data.sendable : 0,
              })}
              <Check className="ms-2 h-4 w-4" aria-hidden="true" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
